<template>
  <ul class="nav">
    <NavLink
      v-for="item in filteredNavigationItems"
      :key="item.index"
      :activeItem="activeItem"
      :header="item.header"
      :link="item.link"
      :iconName="item.iconName"
      :index="item.index"
      :isHeader="item.isHeader"
      :resourceType="item.resourceType"
      :requiredActionPermissions="item.requiredActionPermissions"
      :childrenLinks="item.childrenLinks"
      @submenu-clicked="handleSubmenuClick"
    />
  </ul>
</template>

<script>
import NavLink from './NavLink/NavLink';
import { mapGetters } from 'vuex';
import { ResourceType, AuthorizableActions } from '@/helpers/enums.js';
import { hasResourcePermission } from '@/helpers/userCapabilities.js';

export default {
  name: 'AdminNavigation',
  components: { NavLink },
  props: ['activeItem'],
  methods: {
    handleSubmenuClick(childIndex) {
      // This method is needed to capture submenu clicks at the root level
      console.log(`Root level received submenu click: ${childIndex}`);
    },
  },
  computed: {
    ...mapGetters('auth', ['getCurrentUser']),
    ...mapGetters(['getUser']),
    ...mapGetters('policies', ['getUserPolicies']),
    user() {
      return this.getUser(this.getCurrentUser.id);
    },
    isAdmin() {
      return this.getCurrentUser.role === 'ADMIN';
    },
    userPolicies() {
      return this.getUserPolicies;
    },
    filteredNavigationItems() {
      const items = [
        {
          header: 'Dashboard',
          link: '/dashboard',
          iconName: 'flaticon-home',
          index: 'dashboard',
          isHeader: true,
          resourceType: ResourceType.PROJECT,
          requiredActionPermissions: [AuthorizableActions.LIST, AuthorizableActions.READ],
        },
        {
          header: 'Projekte',
          link: '/projekte',
          iconName: 'flaticon-star',
          index: 'projects',
          isHeader: true,
          resourceType: ResourceType.PROJECT,
          requiredActionPermissions: [AuthorizableActions.LIST, AuthorizableActions.READ],
        },
        {
          header: 'Terminplanung',
          link: '/planungskalender',
          iconName: 'flaticon-calendar',
          index: 'appointment-planning',
          isHeader: true,
          resourceType: ResourceType.APPOINTMENT,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
        {
          header: 'Termin Slots',
          link: '/terminkapazitaet',
          iconName: 'flaticon-cloud',
          index: 'appointment-slots',
          isHeader: true,
          resourceType: ResourceType.APPOINTMENT_CAPACITY,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
        {
          header: 'Aufgaben',
          link: '/aufgaben',
          iconName: 'flaticon-umbrella',
          index: 'tasks',
          isHeader: true,
          resourceType: ResourceType.TASK,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
        {
          header: 'Kalender',
          link: '/kalender',
          iconName: 'flaticon-calendar',
          index: 'calendar',
          isHeader: true,
          resourceType: ResourceType.APPOINTMENT,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
        {
          header: 'Sales',
          link: '/sales/overview',
          iconName: 'flaticon-binoculars',
          index: 'sales',
          isHeader: true,
          resourceType: ResourceType.SALES,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
        {
          header: 'Sales Teams',
          link: '/sales/teams',
          iconName: 'flaticon-users-1',
          index: 'sales-teams',
          isHeader: true,
          resourceType: ResourceType.SALES,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
        {
          header: 'Personalverwaltung',
          link: '/personalverwaltung',
          iconName: 'flaticon-users',
          index: 'hr',
          isHeader: true,
          resourceType: ResourceType.HUMAN_RESOURCE,
          requiredActionPermissions: [AuthorizableActions.LIST],
          childrenLinks: [
            {
              header: 'Benutzer',
              link: '/benutzer',
              index: 'users',
              resourceType: ResourceType.HUMAN_RESOURCE,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
            {
              header: 'Mitarbeiter',
              link: '/mitarbeiter',
              index: 'employees',
              resourceType: ResourceType.HUMAN_RESOURCE,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
            {
              header: 'Mandanten',
              link: '/mandanten',
              index: 'tenants',
              resourceType: ResourceType.CLIENT,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
            {
              header: 'Auftraggeber',
              link: '/auftraggeber',
              index: 'clients',
              resourceType: ResourceType.EMPLOYER,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
            {
              header: 'Elektrofachkräfte',
              link: '/elektrofachkraft',
              index: 'electricians',
              resourceType: ResourceType.ELECTRICIAN,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
            {
              header: 'Messstellenbetreiber',
              link: '/messstellenbetreiber',
              index: 'meter-operators',
              resourceType: ResourceType.MEASUREMENT_POINT_OPERATOR,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
            {
              header: 'Geschäftspartner',
              link: '/geschaeftspartner',
              index: 'parties',
              resourceType: ResourceType.PARTY,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
            {
              header: 'Zeiterfassung',
              link: '/zeiterfassung/',
              iconName: 'flaticon-binoculars',
              index: 'time-tracking',
              isHeader: true,
              resourceType: ResourceType.TIME_TRACKING,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
            {
              header: 'Auslastung',
              link: '/mitarbeiter/auslastung',
              index: 'employee-utilization',
              resourceType: ResourceType.HUMAN_RESOURCE,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
            {
              header: 'GPS Daten',
              link: '/gps',
              index: 'gps-data',
              resourceType: ResourceType.TIME_TRACKING,
              requiredActionPermissions: [AuthorizableActions.LIST, AuthorizableActions.READ],
            },
            {
              header: 'Mitarbeiterantrag',
              link: '/mitarbeiterantrag',
              index: 'employeeRequests',
              resourceType: ResourceType.POLICY,
              requiredActionPermissions: [AuthorizableActions.LIST, AuthorizableActions.READ],
            },
            {
              header: 'Bewerber',
              link: '/bewerber',
              iconName: 'flaticon-radar',
              index: 'applicants',
              isHeader: true,
              resourceType: ResourceType.APPLICANT,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
            {
              header: 'Zugangskontrolle',
              link: '/zugangskontrolle',
              index: 'access-control',
              resourceType: ResourceType.POLICY,
              requiredActionPermissions: [AuthorizableActions.LIST, AuthorizableActions.READ],
            },
            {
              header: 'Rollen',
              link: '/zugangskontrolle/rollen',
              index: 'organization-roles',
              resourceType: ResourceType.POLICY,
              requiredActionPermissions: [AuthorizableActions.LIST, AuthorizableActions.READ],
            },
          ],
        },
        // {
        //   header: 'Anlagen',
        //   link: '/anlagen',
        //   iconName: 'flaticon-power',
        //   index: 'installations',
        //   isHeader: true,
        //   resourceType: ResourceType.SYSTEM,
        //   requiredActionPermissions: [AuthorizableActions.LIST],
        //   childrenLinks: [
        //     {
        //       header: 'Batteriespeicher',
        //       link: '/batterien',
        //       index: 'batteries',
        //       resourceType: ResourceType.SYSTEM,
        //       requiredActionPermissions: [AuthorizableActions.LIST],
        //     },
        //     {
        //       header: 'Wechselrichter',
        //       link: '/wechselrichter',
        //       index: 'inverters',
        //       resourceType: ResourceType.SYSTEM,
        //       requiredActionPermissions: [AuthorizableActions.LIST],
        //     },
        //     {
        //       header: 'Module',
        //       link: '/module',
        //       index: 'modules',
        //       resourceType: ResourceType.SYSTEM,
        //       requiredActionPermissions: [AuthorizableActions.LIST],
        //     },
        //     {
        //       header: 'EnFluRi',
        //       link: '/energieflussrichtungssensoren',
        //       index: 'energy-flow-sensors',
        //       resourceType: ResourceType.SYSTEM,
        //       requiredActionPermissions: [AuthorizableActions.LIST],
        //     },
        //     {
        //       header: 'Wallboxen',
        //       link: '/wallboxen',
        //       index: 'wallboxes',
        //       resourceType: ResourceType.SYSTEM,
        //       requiredActionPermissions: [AuthorizableActions.LIST],
        //     },
        //   ],
        // },
        {
          header: 'Material',
          link: '/material',
          iconName: 'flaticon-price-tag',
          index: 'material',
          isHeader: true,
          resourceType: ResourceType.MATERIAL,
          requiredActionPermissions: [AuthorizableActions.LIST],
          childrenLinks: [
            {
              header: 'Artikel',
              link: '/material/stammdaten',
              index: 'material-master',
              resourceType: ResourceType.MATERIAL,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
            {
              header: 'Lager',
              link: '/material/lager',
              index: 'inventory',
              resourceType: ResourceType.MATERIAL,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
            {
              header: 'Inventar',
              link: '/material/inventar',
              index: 'item-inventories',
              resourceType: ResourceType.MATERIAL,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
            {
              header: 'Transaktionen',
              link: '/material/transaktionen',
              index: 'item-transactions',
              resourceType: ResourceType.MATERIAL,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
            {
              header: 'Bestellungen',
              link: '/material/bestellungen',
              index: 'orders',
              resourceType: ResourceType.MATERIAL,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
            {
              header: 'Pakete',
              link: '/material/pakete',
              index: 'item-packages',
              resourceType: ResourceType.MATERIAL,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
            {
              header: 'Lieferanten',
              link: '/material/lieferanten',
              index: 'suppliers',
              resourceType: ResourceType.MATERIAL,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
          ],
        },
        {
          header: 'Buchhaltung',
          link: '/buchhaltung',
          iconName: 'flaticon-file',
          index: 'accounting',
          isHeader: true,
          resourceType: ResourceType.ACCOUNTING,
          requiredActionPermissions: [AuthorizableActions.LIST],
          childrenLinks: [
            {
              header: 'Rechnungen',
              link: '/buchhaltung/rechnungen',
              index: 'invoices',
              resourceType: ResourceType.ACCOUNTING,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
          ],
        },
        {
          header: 'Berichte',
          link: '/berichte',
          iconName: 'flaticon-diploma',
          index: 'reports',
          isHeader: true,
          resourceType: ResourceType.REPORT,
          requiredActionPermissions: [AuthorizableActions.LIST],
          childrenLinks: [
            {
              header: 'Beispielbilder',
              link: '/berichte/beispielbilder',
              index: 'report-examples',
              resourceType: ResourceType.REPORT_TEMPLATE_IMAGE,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
          ],
        },
        {
          header: 'EVU',
          link: '/evu',
          iconName: 'flaticon-battery',
          index: 'evu',
          isHeader: true,
          resourceType: ResourceType.POWER_COMPANY,
          requiredActionPermissions: [AuthorizableActions.LIST],
          childrenLinks: [
            {
              header: 'EVU',
              link: '/evu',
              index: 'evu',
              resourceType: ResourceType.POWER_COMPANY,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
            {
              header: 'Dokumenten-Editor',
              link: '/dokumente',
              iconName: 'flaticon-document',
              index: 'editor',
              isHeader: true,
              resourceType: ResourceType.POWER_COMPANY,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
          ],
        },
        // {
        //   header: 'Buchhaltung',
        //   link: '/buchhaltung',
        //   iconName: 'flaticon-note',
        //   index: 'accounting',
        //   isHeader: true,
        //   resourceType: ResourceType.OFFER,
        //   requiredActionPermissions: [AuthorizableActions.LIST],
        //   childrenLinks: [
        //     {
        //       header: 'Angebote',
        //       link: '/angebote',
        //       index: 'offers',
        //       resourceType: ResourceType.ACADEMY,
        //       requiredActionPermissions: [AuthorizableActions.LIST],
        //     },
        //     {
        //       header: 'Angebotspositionen-Vorlagen',
        //       link: '/angebotspositionen-vorlagen',
        //       index: 'offer-position-boilerplates',
        //       resourceType: ResourceType.OFFER_POSITION_BOILERPLATE,
        //       requiredActionPermissions: [AuthorizableActions.LIST],
        //     },
        //   ],
        // },
        {
          header: 'Academy',
          link: '/academy',
          iconName: 'flaticon-diploma',
          index: 'academy',
          isHeader: true,
          resourceType: ResourceType.ACADEMY,
          requiredActionPermissions: [AuthorizableActions.LIST],
          childrenLinks: [
            {
              header: 'Course',
              link: '/academy/kurse',
              index: 'courses',
              resourceType: ResourceType.ACADEMY,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
            {
              header: 'Schüler',
              link: '/academy/schueler',
              index: 'students',
              resourceType: ResourceType.ACADEMY,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
            {
              header: 'Trainer',
              link: '/academy/trainer',
              index: 'trainers',
              resourceType: ResourceType.ACADEMY,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
            {
              header: 'Academy',
              link: '/academy',
              index: 'academy-home',
              resourceType: ResourceType.ACADEMY,
              requiredActionPermissions: [AuthorizableActions.READ],
            },
            {
              header: 'Stundenplantermine',
              link: '/academy/lehrplan',
              index: 'academy-schedule',
              resourceType: ResourceType.ACADEMY,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
            {
              header: 'Calendar',
              link: '/academy/stundenplan',
              index: 'academy-calendar',
              resourceType: ResourceType.ACADEMY,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
            {
              header: 'Lehrinhalte',
              link: '/academy/lehrmaterial',
              index: 'teaching-materials',
              resourceType: ResourceType.ACADEMY,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
            {
              header: 'Klasseninhalte',
              link: '/academy/klassen',
              index: 'class-contents',
              resourceType: ResourceType.ACADEMY,
              requiredActionPermissions: [AuthorizableActions.LIST],
            },
          ],
        },
        {
          header: 'Aktivitätsprotokoll',
          link: '/aktivitaetsprotokoll',
          iconName: 'flaticon-note',
          index: 'activity-log',
          isHeader: true,
          resourceType: ResourceType.AUDIT,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
        {
          header: 'Logging',
          link: '/logging',
          iconName: 'flaticon-file',
          index: 'logging',
          isHeader: true,
          resourceType: ResourceType.AUDIT,
          requiredActionPermissions: [AuthorizableActions.LIST],
        },
        {
          header: 'Nachrichten',
          link: '/nachrichten',
          iconName: 'flaticon-notification',
          index: 'nachrichten',
          isHeader: true,
          resourceType: ResourceType.PUSH_NOTIFICATION,
          requiredActionPermissions: [AuthorizableActions.READ, AuthorizableActions.CREATE],
        },
        {
          header: 'Einstellungen',
          link: '/einstellungen',
          iconName: 'flaticon-settings-5',
          index: 'settings',
          isHeader: true,
          resourceType: ResourceType.SETTINGS,
          requiredActionPermissions: [AuthorizableActions.UPDATE],
        },
        {
          header: 'Updates',
          link: '/updates',
          iconName: 'flaticon-idea',
          index: 'updates',
          isHeader: true,
          resourceType: ResourceType.UPDATE,
          requiredActionPermissions: [AuthorizableActions.READ],
        },
      ];

      // console.log('User:', this.getCurrentUser);
      // console.log('Policies:', this.userPolicies);

      // Build a mutable nav list to tailor per role
      let navItems = items;

      // For AUDITOR: remove HR section and add a top-level Time Tracking item
      if (this.getCurrentUser && this.getCurrentUser.role === 'AUDITOR') {
        navItems = items.filter((i) => i.index !== 'hr');
        if (!navItems.find((i) => i.index === 'time-tracking')) {
          navItems.push({
            header: 'Zeiterfassung',
            link: '/zeiterfassung/',
            iconName: 'flaticon-time',
            index: 'time-tracking',
            isHeader: true,
            resourceType: ResourceType.TIME_TRACKING,
            requiredActionPermissions: [AuthorizableActions.LIST],
          });
        }
      }

      return navItems.filter((item) => {
        // Admin sees everything
        if (this.isAdmin) return true;

        // INSTALLATION users should not see any navigation items
        if (
          this.getCurrentUser.authorizationAttributes.role === 'INSTALLATION' &&
          this.getCurrentUser.authorizationAttributes.department === 'INSTALLATION'
        ) {
          return false;
        }

        // Hide specific children for non-admins in a single check (except MANAGER/CONTROLLING)
        if (!this.isAdmin && item.childrenLinks) {
          const isManagerControlling =
            this.getCurrentUser.authorizationAttributes.role === 'MANAGER' &&
            this.getCurrentUser.authorizationAttributes.department === 'CONTROLLING';

          item.childrenLinks = item.childrenLinks.filter((child) => {
            // For MANAGER/CONTROLLING, only hide access-control, not employees
            if (isManagerControlling) {
              return child.index !== 'access-control';
            }
            // For other non-admins, hide both employees and access-control
            return child.index !== 'employees' && child.index !== 'access-control';
          });
        }

        // Hide users and tenants for MANAGER/CONTROLLING users
        if (
          this.getCurrentUser.authorizationAttributes.role === 'MANAGER' &&
          this.getCurrentUser.authorizationAttributes.department === 'CONTROLLING' &&
          item.childrenLinks
        ) {
          item.childrenLinks = item.childrenLinks.filter(
            (child) => child.index !== 'users' && child.index !== 'tenants'
          );
        }

        // Guest users cannot see Dashboard and Anlagen
        if (
          this.getCurrentUser.authorizationAttributes.role === 'GUEST' &&
          (item.index === 'installations' ||
            item.index === 'evu' ||
            item.index === 'appointment-planning' ||
            item.index === 'calendar' ||
            item.index === 'material')
        ) {
          return false;
        }

        // Role: Standard, Department: SALES: users only see Sales
        if (
          this.getCurrentUser.authorizationAttributes.role === 'ROLE_STANDARD' &&
          this.getCurrentUser.authorizationAttributes.department === 'SALES' &&
          (item.index === 'installations' ||
            item.index === 'evu' ||
            item.index === 'appointment-planning' ||
            item.index === 'calendar' ||
            item.index === 'material' ||
            item.index === 'dashboard' ||
            item.index === 'projects' ||
            item.index === 'sales-teams')
        ) {
          return false;
        }

        // Role: Standard, Department: CONTROLLING: users only Projects
        if (
          this.getCurrentUser.authorizationAttributes.role === 'ROLE_STANDARD' &&
          this.getCurrentUser.authorizationAttributes.department === 'CONTROLLING' &&
          (item.index === 'installations' ||
            item.index === 'evu' ||
            item.index === 'appointment-planning' ||
            item.index === 'calendar' ||
            item.index === 'material' ||
            item.index === 'dashboard' ||
            item.index === 'tasks' ||
            item.index === 'sales-teams')
        ) {
          return false;
        }

        // Deparment: SALES  / UserRole: TEAM_LEAD only sees sales and sales teams
        if (
          this.getCurrentUser.authorizationAttributes.department === 'SALES' &&
          this.getCurrentUser.authorizationAttributes.role === 'TEAM_LEAD' &&
          (item.index === 'evu' ||
            item.index === 'dashboard' ||
            item.index === 'projects' ||
            item.index === 'material' ||
            item.index === 'calendar')
        ) {
          return false;
        }

        // Auditor users only see Appointments and Time Tracking
        if (
          this.getCurrentUser.authorizationAttributes.role === 'AUDITOR' &&
          (item.index === 'evu' ||
            item.index === 'dashboard' ||
            item.index === 'projects' ||
            item.index === 'material' ||
            item.index === 'calendar')
        ) {
          return false;
        }

        // Check if user has access to this item based on policies
        return hasResourcePermission(
          this.getCurrentUser,
          item.resourceType,
          item.requiredActionPermissions
        );
      });
    },
  },
  created() {},
};
</script>

<style>
.sub-menu .router-link-active .icon {
  display: none;
}
/* Webkit browsers (Chrome, Safari, newer versions of Opera and Edge) */
::-webkit-scrollbar {
  width: 15px;
}

::-webkit-scrollbar-thumb {
  background-color: #98269f;
  border-radius: 6px;
}

::-webkit-scrollbar-track {
  background-color: #f1f1f1;
}

/* Firefox */
* {
  scrollbar-width: thick;
  scrollbar-color: #98269f #f1f1f1;
}
</style>
